/**
 * Theme Helper Utilities
 * 
 * Provides helper functions for consistent theme usage across the application.
 * Use these instead of hardcoded colors to ensure theme consistency.
 */

import { Theme } from '@mui/material/styles'
import { tokens } from '../theme/tokens'

/**
 * Get status color from theme
 * Maps status strings to theme colors
 */
export const getStatusColor = (
  status: string,
  theme?: Theme
): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  const statusMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    // Equipment/General statuses
    active: 'success',
    inactive: 'default',
    maintenance: 'warning',
    retired: 'error',
    
    // Inspection statuses
    scheduled: 'info',
    inProgress: 'warning',
    completed: 'success',
    approved: 'success',
    rejected: 'error',
    overdue: 'error',
    
    // Maintenance statuses
    open: 'warning',
    closed: 'success',
    critical: 'error',
    
    // General statuses
    pending: 'warning',
    success: 'success',
    failed: 'error',
    cancelled: 'default',
  }
  
  return statusMap[status.toLowerCase()] || 'default'
}

/**
 * Get status color hex value from theme
 * Returns the actual color value for use in charts, etc.
 */
export const getStatusColorValue = (status: string, theme: Theme): string => {
  const colorKey = getStatusColor(status)
  
  const colorMap: Record<string, string> = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    default: theme.palette.grey[500],
  }
  
  return colorMap[colorKey] || theme.palette.grey[500]
}

/**
 * Get health score color based on score value
 */
export const getHealthScoreColor = (score: number, theme: Theme): string => {
  if (score >= 90) return theme.palette.success.main
  if (score >= 70) return theme.palette.warning.main
  if (score >= 50) return theme.palette.error.main
  return theme.palette.error.dark
}

/**
 * Get severity icon color
 */
export const getSeverityColor = (
  severity: 'critical' | 'high' | 'medium' | 'low' | 'warning',
  theme: Theme
): string => {
  const severityMap: Record<string, string> = {
    critical: theme.palette.error.main,
    high: theme.palette.warning.main,
    medium: theme.palette.info.main,
    low: theme.palette.success.main,
    warning: theme.palette.warning.main,
  }
  
  return severityMap[severity] || theme.palette.grey[500]
}

/**
 * Get gradient background for cards
 * Provides consistent gradient styles
 */
export const getCardGradient = (
  variant: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info',
  theme: Theme
): string => {
  const gradients: Record<string, string> = {
    primary: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    secondary: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
    success: `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`,
    warning: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.main} 100%)`,
    error: `linear-gradient(135deg, ${theme.palette.error.light} 0%, ${theme.palette.error.main} 100%)`,
    info: `linear-gradient(135deg, ${theme.palette.info.light} 0%, ${theme.palette.info.main} 100%)`,
  }
  
  return gradients[variant] || gradients.primary
}

/**
 * Get gauge color based on value and range
 */
export const getGaugeColor = (
  value: number,
  min: number,
  max: number,
  isWithinRange: boolean,
  theme: Theme
): string => {
  return isWithinRange ? theme.palette.success.main : theme.palette.error.main
}

/**
 * Get target color for charts
 */
export const getTargetColor = (theme: Theme): string => {
  return theme.palette.info.main
}

/**
 * Get chart colors palette
 * Returns an array of colors for multi-series charts
 */
export const getChartColors = (theme: Theme): string[] => {
  return [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.primary.light,
    theme.palette.secondary.light,
  ]
}

/**
 * Get placeholder color for images
 */
export const getPlaceholderColor = (theme: Theme): string => {
  return theme.palette.mode === 'dark' 
    ? theme.palette.grey[800] 
    : theme.palette.grey[300]
}

/**
 * Get background gradient for auth pages
 */
export const getAuthGradient = (theme: Theme): string => {
  return theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
}

/**
 * Get ring color based on achievement
 */
export const getRingColor = (
  achieved: boolean | undefined,
  defaultColor: string,
  theme: Theme
): string => {
  if (achieved === undefined) return defaultColor
  return achieved ? theme.palette.success.main : theme.palette.warning.main
}

/**
 * Export tokens for direct access
 */
export { tokens }
